/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import ErrorActions from '../Errors/ErrorsActions';
import { createAction, Dispatch } from '@reduxjs/toolkit';
import { IMachine, IMachineData } from '@umati/dashboard-types';
import Api, { Machine } from 'Api/Api';

export enum MachineActionTypes {
  Query = 'Machines_Query',
  Queried = 'Machines_Queried',
  Updated = 'Machines_Updated',
  Delete = 'Machines_Delete',
  Deleted = 'Machines_Deleted',
  ShortIdEdit = 'Machines_ShortId_Edit',
  ShortIdEdited = 'Machines_ShortId_Edited',
}

export interface IShortIdEditPayload {
  id: IMachine['Id'];
  shortId: IMachine['ShortId'];
}

type SafeJson = unknown;

function parseMachineData(data: string | null | undefined): IMachineData {
  if (typeof data !== 'string') {
    return {
      Model: { text: '', locale: 'en' },
      Location: '',
      DeviceClass: '',
      ProductCode: '',
      Manufacturer: { text: '', locale: 'en' },
      SerialNumber: '',
      SoftwareRevision: '',
      ProductInstanceUri: '',
      YearOfConstruction: 0,
    };
  }
  try {
    const parsed = JSON.parse(data) as Partial<IMachineData> | SafeJson;
    const p = (parsed as Partial<IMachineData>) ?? {};

    return {
      Model: p.Model ?? { text: '', locale: 'en' },
      Location: p.Location ?? '',
      DeviceClass: p.DeviceClass ?? '',
      ProductCode: p.ProductCode ?? '',
      Manufacturer: p.Manufacturer ?? { text: '', locale: 'en' },
      SerialNumber: p.SerialNumber ?? '',
      SoftwareRevision: p.SoftwareRevision ?? '',
      ProductInstanceUri: p.ProductInstanceUri ?? '',
      YearOfConstruction: typeof p.YearOfConstruction === 'number' ? p.YearOfConstruction : 0,
    };
  } catch {
    return {
      Model: { text: '', locale: 'en' },
      Location: '',
      DeviceClass: '',
      ProductCode: '',
      Manufacturer: { text: '', locale: 'en' },
      SerialNumber: '',
      SoftwareRevision: '',
      ProductInstanceUri: '',
      YearOfConstruction: 0,
    };
  }
}

function toIMachine(raw: Machine): IMachine {
  return {
    Id: raw.Id ?? 0,
    TypeDefinition: raw.TypeDefinition ?? '',
    MachineId: raw.MachineId ?? '',
    ShortId: raw.ShortId ?? '',
    ImagePath: raw.ImagePath ?? null,
    Data: parseMachineData(raw.Data as unknown as string | null | undefined),
    Positions: undefined,
    Online: undefined,
    Favorite: undefined,
    ParentId: raw.ParentId ?? undefined,
    ClientId: raw.ClientId ?? undefined,
  };
}

function deserializeMachinesFromBackendRequestSafe(data: Machine[] | undefined | null): IMachine[] {
  if (!Array.isArray(data)) return [];
  return data.map((m) => toIMachine(m));
}

export const MachineAction = {
  Query: createAction(MachineActionTypes.Query),
  Queried: createAction<IMachine[]>(MachineActionTypes.Queried),
  Updated: createAction<IMachine>(MachineActionTypes.Updated),
  Delete: createAction<IMachine['Id']>(MachineActionTypes.Delete),
  Deleted: createAction<IMachine>(MachineActionTypes.Deleted),
  ShortId_Edited: createAction<IMachine>(MachineActionTypes.ShortIdEdited),
  ShortId_Edit: createAction<IShortIdEditPayload>(MachineActionTypes.ShortIdEdit),
};

export default class MachinesActions {
  static QueryMachines() {
    return function (dispatch: Dispatch) {
      dispatch(MachineAction.Query());
      Api.Machines.getMachines()
        .then((data) => dispatch(MachineAction.Queried(deserializeMachinesFromBackendRequestSafe(data.data))))
        .catch((err) => dispatch(ErrorActions.Error(err?.error)));
    };
  }

  static ResetShortIds() {
    return function (dispatch: Dispatch) {
      Api.Machines.resetShortIds()
        .then(() => {
          MachinesActions.QueryMachines()(dispatch);
        })
        .catch((err) => dispatch(ErrorActions.Error(err?.error)));
    };
  }

  static EditShortId(id: IMachine['Id'], shortId: IMachine['ShortId']) {
    return function (dispatch: Dispatch) {
      dispatch(MachineAction.ShortId_Edit({ id, shortId }));
      Api.Machines.editShortId(id, shortId)
        .then((data) => dispatch(MachineAction.ShortId_Edited(toIMachine(data.data))))
        .catch((data) => dispatch(ErrorActions.Error(data.error)));
    };
  }

  static Delete(id: IMachine['Id']) {
    return function (dispatch: Dispatch) {
      dispatch(MachineAction.Delete(id));
      Api.Machines.deleteMachine(id)
        .then((data) => dispatch(MachineAction.Deleted(toIMachine(data.data))))
        .catch((data) => dispatch(ErrorActions.Error(data.error)));
    };
  }

  static UploadImg(id: IMachine['Id'], file: File) {
    return function (dispatch: Dispatch) {
      const postData = new FormData();
      postData.append('file', file);
      postData.append('id', String(id));
      Api.Machines.uploadImg(id, file)
        .then((data) => dispatch(MachineAction.Updated(toIMachine(data.data))))
        .catch((data) => dispatch(ErrorActions.Error(data.error)));
    };
  }
}
